// @flow
import React from 'react';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { AppLink } from '@finect/tabular-core/AppLink';
import { H2, H3 } from '@finect/tabular-core/Headings';
import { ArticleDetail, ArticleSide, Video, ArticleCTA, LazyPill } from '../../../components';

type Props = {
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Hablar = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="¿De qué tienes que hablar con tu asesor?"
      metaTitle="¿De qué temas debes hablar con tu asesor financiero? Conversaciones clave"
      redirectTo={redirectTo}
      toggleModal={toggleModal}
      image="43bf42e65b7548e3a477e219fdb685c0"
      schemaORG={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Qué tan personales deben ser las conversaciones con un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Las conversaciones con tu asesor deben ser tan personales como sea necesario para que pueda comprender tu situación completa. Esto incluye tus metas, estilo de vida, prioridades familiares y tolerancia al riesgo. Cuanto más abierto seas, mejor podrá diseñar un plan que se ajuste a tus necesidades y objetivos financieros.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Es necesario hablar de mis deudas con el asesor?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, es fundamental informar a tu asesor sobre todas tus deudas, tanto personales como hipotecarias o de consumo. Esto permite evaluar tu capacidad de ahorro, establecer prioridades y diseñar un plan de inversión y gestión financiera realista y sostenible.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Puedo hablar con mi asesor sobre decisiones importantes, como comprar una casa o cambiar de trabajo?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutamente. Tu asesor financiero puede ayudarte a analizar el impacto financiero de decisiones importantes, como adquirir una vivienda, iniciar un negocio, cambiar de empleo o planificar la educación de tus hijos. Discutir estos eventos permite ajustar tu plan financiero y mantener tus objetivos a largo plazo en perspectiva.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Qué pasa si no entiendo bien lo que me dice mi asesor?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Un buen asesor financiero tiene la capacidad de explicar conceptos complejos de manera sencilla. Si algo no te queda claro, debes pedir que te lo expliquen de nuevo o con ejemplos prácticos. La claridad en la comunicación es esencial para tomar decisiones informadas y confiar en el plan propuesto.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Con qué frecuencia debo actualizar a mi asesor sobre cambios en mi vida financiera?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Debes mantener informado a tu asesor siempre que haya cambios significativos en tu situación financiera o personal, como matrimonio, nacimiento de hijos, cambios de empleo, herencias o variaciones importantes en tus ingresos o gastos. Además, es recomendable revisar tu plan de manera periódica, al menos una vez al año, para asegurarte de que sigue alineado con tus objetivos.'
            }
          }
        ]
      }}
    >
      <Paragraph>
        Piensa por un momento en cómo sería la conversación que vas a tener con un{' '}
        <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=de_que_hablar">
          asesor financiero
        </AppLink>{' '}
        al que has llamado para conocer sus servicios. ¿Qué le pedirías? Seguro que rápidamente se nos va la mente a la
        rentabilidad: “¿Cuánto voy a poder ganar siguiendo tus consejos?”. Ese enfoque es un error.
      </Paragraph>
      <ArticleSide full position="right">
        <Video
          id="b5ourXeGsA0"
          src="https://www.youtube.com/embed/b5ourXeGsA0?hd=1&showinfo=0&controls=0&rel=0&enablejsapi=1"
          caption="Carlos Farrás, DPM Finanzas."
        />
      </ArticleSide>
      <Paragraph>
        Esto sería como ir directamente a la consulta del médico y decirle “Quiero que me de usted la mejor medicina”.
        Se quedará a cuadros y te dirá que eso no tiene sentido, que primero necesita saber de qué estás enfermo. Y que
        para ello será imprescindible hacerte unas cuantas preguntas, auscultarte, hacerte unos análisis de sangre o una
        ecografía…
      </Paragraph>
      <Paragraph>
        Con un{' '}
        <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=de_que_hablar">
          asesor o experto financiero
        </AppLink>{' '}
        ocurre exactamente lo mismo: para ofrecerte un plan efectivo, necesita conocer tu vida, tus metas, tus
        prioridades y tus miedos antes de hablar de productos o rentabilidades.
      </Paragraph>

      <section>
        <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />
      </section>

      <section style={{ marginTop: '60px' }}>
        <H2>¿Por qué es importante tener conversaciones profundas con tu asesor?</H2>
        <Paragraph>
          Las conversaciones profundas con el profesional son la base para un buen plan financiero. Especialmente en
          nuestras primeras reuniones. De hecho, sólo deberíamos hablar de rentabilidades al final. Los primeros
          encuentros deberían girar mucho más en torno a nuestra vida, nuestros objetivos, nuestros anhelos o nuestros
          miedos.
        </Paragraph>
        <Paragraph>
          Es decir, abordar nuestros proyectos, nuestras ilusiones, nuestra forma de ver la vida.. Y no tanto centrarnos
          en productos financieros, eso ya vendrá después. El experto debe conocernos bien para poder diseñar bien
          después su propuesta de asesoramiento financiero y ofrecernos las mejores opciones.
        </Paragraph>

        <H3>Tu asesor solo puede ayudarte si conoce tu situación real</H3>

        <Paragraph>
          Un asesor solo puede diseñar estrategias que funcionen si comprende tu contexto completo: tus ingresos,
          gastos, deudas, patrimonio y estilo de vida. Todo ello es clave para ver exactamente nuestras necesidades,
          plantearnos una propuesta de ahorro e inversión y seguir de cerca el cumplimiento de los hitos que nos hayamos
          marcado.{' '}
        </Paragraph>
        <Paragraph>
          Y, una vez que estas bases están sentadas, ya sí, hablar de rentabilidades y volatilidades, pero sabiendo a
          qué velocidad queremos ir y por qué caminos, para llegar a nuestro destino. Porque cuanta más información
          tenga sobre ti, mejor podrá adaptar sus recomendaciones. Esto incluye entender cómo valoras la estabilidad
          frente al riesgo, tus prioridades y tus proyectos.
        </Paragraph>

        <H3>Más información = mejores estrategias</H3>

        <Paragraph>
          Cuando compartes tu visión de la vida y tus objetivos financieros, el asesor puede crear un plan
          personalizado. Saber si para ti es más importante la educación de tus hijos que comprar una casa más grande, o
          si priorizas un colchón de seguridad antes que viajar cada verano, si aspiras a jubilarte pronto o, al revés,
          te gusta trabajar y mantenerte con actividad… Es decir, permite que el plan de ahorro e inversión se ajuste
          exactamente a lo que quieres lograr.{' '}
        </Paragraph>
      </section>

      <section>
        <H2>Temas esenciales que deberías tratar con tu asesor financiero</H2>
        <Paragraph>
          Para que tu asesor pueda elaborar un plan financiero realmente útil, es fundamental que tengas conversaciones
          abiertas y detalladas sobre aspectos concretos de tu vida y tu patrimonio. Cuanta más información relevante
          compartas, más personalizado y eficaz será el plan.
        </Paragraph>
        <H3>Tus objetivos personales y financieros (corto, mediano y largo plazo)</H3>
        <Paragraph>
          No se trata solo de cifras, sino de lo que quieres lograr en la vida. Define metas claras, como comprar una
          vivienda, financiar estudios, jubilarte con tranquilidad o incluso permitirte ciertos lujos. Cada objetivo
          marcará la estrategia de ahorro e inversión que tu asesor diseñe para ti.
        </Paragraph>
        <H3>Ingresos, gastos y deudas actuales</H3>
        <Paragraph>
          Un análisis realista de tus ingresos, gastos y deudas permite al asesor calcular tu capacidad de ahorro y
          determinar cómo distribuir los recursos de manera eficiente. Esto ayuda a establecer prioridades y a evitar
          desequilibrios financieros.
        </Paragraph>
        <H3>Ahorro e inversión: cuánto, dónde y para qué</H3>
        <Paragraph>
          Comparte tus hábitos de ahorro, la cantidad que puedes destinar a inversión y tus preferencias de riesgo. Esta
          información es la base para estructurar carteras adecuadas y establecer un plan de seguimiento coherente con
          tus metas.
        </Paragraph>
        <H3>Riesgos y seguros (protección personal y patrimonial)</H3>
        <Paragraph>
          Habla sobre la protección frente a imprevistos, tanto personales como patrimoniales. Seguros de vida, salud o
          propiedad, así como planes de contingencia, son elementos clave que tu asesor debe conocer para protegerte
          frente a riesgos financieros.
        </Paragraph>
        <H3>Planificación para la jubilación</H3>
        <Paragraph>
          Define tus expectativas sobre cuándo quieres jubilarte y cómo deseas vivir esa etapa. Esta información permite
          establecer una estrategia de ahorro e inversión a largo plazo que garantice estabilidad futura.
        </Paragraph>
        <H3>Impuestos y planificación fiscal</H3>
        <Paragraph>
          Analiza con tu asesor cómo optimizar tus decisiones financieras considerando la fiscalidad. Planificar
          impuestos y beneficios fiscales es esencial para que tus inversiones y ahorros rindan al máximo.
        </Paragraph>
        <H3>Cambios importantes en tu vida (matrimonio, hijos, herencias, etc.)</H3>
        <Paragraph>
          Eventos significativos pueden alterar tus necesidades financieras y tu estrategia de inversión. Compartir
          estos cambios permite al asesor ajustar el plan a tiempo y mantenerlo alineado con tus objetivos.
        </Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>Preguntas que deberías hacerle a tu asesor</H2>
        <Paragraph>
          Hacer las preguntas correctas garantiza que el asesor entienda tu situación y diseñe un plan que realmente
          funcione para ti.
        </Paragraph>
        <Paragraph>
          <b>¿Estoy en el camino correcto hacia mis objetivos financieros?</b>
        </Paragraph>
        <Paragraph>
          Consulta si tu plan actual está alineado con tus metas y si necesitas ajustar tus estrategias.
        </Paragraph>
        <Paragraph>
          <b>¿Qué puedo hacer para optimizar mis inversiones?</b>
        </Paragraph>
        <Paragraph>
          Pregunta por alternativas de inversión más eficientes, diversificación o cambios en tu cartera para mejorar
          resultados sin asumir riesgos innecesarios.
        </Paragraph>
        <Paragraph>
          <b>¿Cómo me afecta la situación económica actual?</b>
        </Paragraph>
        <Paragraph>
          Un buen asesor te explicará cómo la inflación, los tipos de interés y otros factores macroeconómicos pueden
          influir en tus inversiones y tu planificación financiera.
        </Paragraph>
        <Paragraph>
          <b>¿Qué riesgos financieros debería estar considerando?</b>
        </Paragraph>
        <Paragraph>
          Identificar riesgos potenciales es clave para proteger tu patrimonio. Pregunta qué escenarios adversos podrían
          afectar tu plan y cómo mitigarlos.
        </Paragraph>
        <Paragraph>
          <b>¿Cuáles son tus recomendaciones a corto plazo?</b>
        </Paragraph>
        <Paragraph>
          Además del plan a largo plazo, es útil conocer qué acciones concretas puedes tomar ahora para mejorar tu
          situación financiera.
        </Paragraph>
        <H2>¿Cada cuánto deberías reunirte con tu asesor?</H2>
        <Paragraph>
          La frecuencia y el enfoque de tus reuniones son fundamentales para que el plan financiero funcione y se
          mantenga actualizado.
        </Paragraph>
        <Paragraph>
          <b>Frecuencia ideal de seguimiento</b>
        </Paragraph>
        <Paragraph>
          Aunque depende de tu perfil y objetivos, lo habitual es reunirse al menos una vez al año para revisión general
          y ajustes estratégicos. Algunos clientes prefieren encuentros trimestrales si hay cambios importantes o
          inversiones activas.
        </Paragraph>
        <Paragraph>
          <b>Qué revisar en cada encuentro</b>
        </Paragraph>
        <Paragraph>
          En cada reunión se deben evaluar los resultados obtenidos, analizar los cambios en tus objetivos, revisar
          inversiones y ajustar la estrategia según la evolución de tu vida y del mercado.
        </Paragraph>
        <Paragraph>
          <b>Cuándo actualizar tu plan financiero</b>
        </Paragraph>
        <Paragraph>
          Cualquier cambio significativo en tu vida, como matrimonio, nacimiento de hijos, cambio de trabajo o herencia,
          requiere actualizar tu plan. Además, las variaciones importantes del mercado también pueden justificar
          ajustes.
        </Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>Preguntas frecuentes</H2>

        <Paragraph>
          <b>1. ¿Cómo de personales deben ser las conversaciones con un asesor financiero?</b>
        </Paragraph>
        <Paragraph>
          Las conversaciones con tu asesor deben ser tan personales como sea necesario para que pueda comprender tu
          situación completa. Esto incluye tus metas, estilo de vida, prioridades familiares y tolerancia al riesgo.
          Cuanto más abierto seas, mejor podrá diseñar un plan que se ajuste a tus necesidades y objetivos financieros.
        </Paragraph>
        <Paragraph>
          <b>2. ¿Es necesario hablar de mis deudas con el asesor?</b>
        </Paragraph>
        <Paragraph>
          Sí, es fundamental informar a tu asesor sobre todas tus deudas, tanto personales como hipotecarias o de
          consumo. Esto permite evaluar tu capacidad de ahorro, establecer prioridades y diseñar un plan de inversión y
          gestión financiera realista y sostenible.
        </Paragraph>
        <Paragraph>
          <b>
            3. ¿Puedo hablar con mi asesor sobre decisiones importantes, como comprar una casa o cambiar de trabajo?
          </b>
        </Paragraph>
        <Paragraph>
          Absolutamente. Tu asesor financiero puede ayudarte a analizar el impacto financiero de decisiones importantes,
          como adquirir una vivienda, iniciar un negocio, cambiar de empleo o planificar la educación de tus hijos.
          Discutir estos eventos permite ajustar tu plan financiero y mantener tus objetivos a largo plazo en
          perspectiva.
        </Paragraph>
        <Paragraph>
          <b>4. ¿Qué pasa si no entiendo bien lo que me dice mi asesor?</b>
        </Paragraph>
        <Paragraph>
          Un buen asesor financiero tiene la capacidad de explicar conceptos complejos de manera sencilla. Si algo no te
          queda claro, debes pedir que te lo expliquen de nuevo o con ejemplos prácticos. La claridad en la comunicación
          es esencial para tomar decisiones informadas y confiar en el plan propuesto.
        </Paragraph>
        <Paragraph>
          <b>5. ¿Con qué frecuencia debo actualizar a mi asesor sobre cambios en mi vida financiera?</b>
        </Paragraph>
        <Paragraph>
          Debes mantener informado a tu asesor siempre que haya cambios significativos en tu situación financiera o
          personal, como matrimonio, nacimiento de hijos, cambios de empleo, herencias o variaciones importantes en tus
          ingresos o gastos. Además, es recomendable revisar tu plan de manera periódica, al menos una vez al año, para
          asegurarte de que sigue alineado con tus objetivos.
        </Paragraph>
      </section>
      <LazyPill />
    </ArticleDetail>
  );
};
